import type { Locale } from "./index";

/**
 * Inline SVG flag icons for language selector / locale switchers.
 *
 * Why not emoji 🇵🇱 🇬🇧 🇩🇪 — Windows doesn't render regional indicator
 * symbols as colored flags (it falls back to letter pairs "PL", "GB", "DE"),
 * which looks broken in UI. Inline SVG renders identically across OS/browsers.
 *
 * Uniform viewBox 3:2 (30×20) for all flags — gives consistent visual weight
 * in dropdowns/menus. Official ratios (PL 5:8, GB 1:2, DE 3:5) are slightly
 * stretched to fit, which is the common pattern in flag-icons / Twitter / Apple.
 *
 * **Accept any string code** — beyond app-internal `Locale` (pl/en/de), the
 * `WLanguageVersions` window accepts arbitrary domain languages (fr/es/it/...).
 * Unknown codes render as a 2-letter monogram badge (e.g. `RU`) so layout
 * never breaks.
 *
 * @example
 *   <Flag locale="pl" />
 *   <Flag locale="fr" height={20} />
 */

type FlagProps = {
  locale: Locale | string;
  className?: string;
  /** Display height in px. Width auto from 3:2 ratio. Default 14 -> width 21. */
  height?: number;
};

export function Flag({ locale, className, height = 14 }: FlagProps) {
  switch (locale.toLowerCase()) {
    case "pl":
      return <FlagPL className={className} height={height} />;
    case "en":
    case "gb":
      return <FlagGB className={className} height={height} />;
    case "de":
      return <FlagDE className={className} height={height} />;
    case "fr":
      return <FlagFR className={className} height={height} />;
    case "es":
      return <FlagES className={className} height={height} />;
    case "it":
      return <FlagIT className={className} height={height} />;
    default:
      return <FlagFallback code={locale} className={className} height={height} />;
  }
}

//----//
// Per-country SVG components — all use viewBox 0 0 30 20 (3:2 ratio)
//----//

const VIEWBOX = "0 0 30 20";
const ASPECT = 3 / 2;

function FlagPL({ className, height }: { className?: string; height: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={VIEWBOX}
      height={height}
      width={height * ASPECT}
      className={className}
      aria-label="Flag of Poland"
      role="img"
    >
      <rect width="30" height="10" fill="#fff" />
      <rect y="10" width="30" height="10" fill="#dc143c" />
    </svg>
  );
}

function FlagDE({ className, height }: { className?: string; height: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={VIEWBOX}
      height={height}
      width={height * ASPECT}
      className={className}
      aria-label="Flag of Germany"
      role="img"
    >
      <rect width="30" height="6.667" fill="#000" />
      <rect y="6.667" width="30" height="6.667" fill="#dd0000" />
      <rect y="13.333" width="30" height="6.667" fill="#ffce00" />
    </svg>
  );
}

function FlagGB({ className, height }: { className?: string; height: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={VIEWBOX}
      height={height}
      width={height * ASPECT}
      className={className}
      aria-label="Flag of the United Kingdom"
      role="img"
    >
      <clipPath id="ukFlagClip">
        <path d="M0,0 v20 h30 v-20 z" />
      </clipPath>
      <g clipPath="url(#ukFlagClip)">
        {/* Blue background */}
        <rect width="30" height="20" fill="#012169" />
        {/* White diagonal saltire (Scotland) */}
        <path d="M0,0 L30,20 M30,0 L0,20" stroke="#fff" strokeWidth="4" />
        {/* Red diagonal saltire (Ireland) — narrower, on top of white */}
        <path d="M0,0 L30,20 M30,0 L0,20" stroke="#C8102E" strokeWidth="2.5" />
        {/* White cross (England) */}
        <path d="M15,0 v20 M0,10 h30" stroke="#fff" strokeWidth="6" />
        {/* Red cross (England) */}
        <path d="M15,0 v20 M0,10 h30" stroke="#C8102E" strokeWidth="3.5" />
      </g>
    </svg>
  );
}

function FlagFR({ className, height }: { className?: string; height: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={VIEWBOX}
      height={height}
      width={height * ASPECT}
      className={className}
      aria-label="Flag of France"
      role="img"
    >
      <rect width="10" height="20" fill="#002654" />
      <rect x="10" width="10" height="20" fill="#fff" />
      <rect x="20" width="10" height="20" fill="#ED2939" />
    </svg>
  );
}

function FlagES({ className, height }: { className?: string; height: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={VIEWBOX}
      height={height}
      width={height * ASPECT}
      className={className}
      aria-label="Flag of Spain"
      role="img"
    >
      <rect width="30" height="20" fill="#AA151B" />
      <rect y="5" width="30" height="10" fill="#F1BF00" />
    </svg>
  );
}

function FlagIT({ className, height }: { className?: string; height: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={VIEWBOX}
      height={height}
      width={height * ASPECT}
      className={className}
      aria-label="Flag of Italy"
      role="img"
    >
      <rect width="10" height="20" fill="#009246" />
      <rect x="10" width="10" height="20" fill="#fff" />
      <rect x="20" width="10" height="20" fill="#CE2B37" />
    </svg>
  );
}

/**
 * Fallback dla nieznanego kodu języka — 2-literowy monogram w prostokącie.
 * Zachowuje 3:2 ratio dla zachowania spójności wymiarów z prawdziwymi flagami,
 * dzięki czemu layout dropdown'a/listy języków nie skacze.
 */
function FlagFallback({
  code,
  className,
  height,
}: {
  code: string;
  className?: string;
  height: number;
}) {
  const text = code.slice(0, 2).toUpperCase();
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={VIEWBOX}
      height={height}
      width={height * ASPECT}
      className={className}
      aria-label={`Flag placeholder for ${text}`}
      role="img"
    >
      <rect width="30" height="20" fill="#e5e7eb" />
      <text
        x="15"
        y="14"
        textAnchor="middle"
        fontFamily="system-ui, sans-serif"
        fontSize="10"
        fontWeight="700"
        fill="#374151"
      >
        {text}
      </text>
    </svg>
  );
}
